'use client'

import { useState } from 'react'
import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'
import { useTranslation } from '@/i18n/useTranslation'
import { langPrefix } from '@/i18n/translations'
import { usePathLang } from '@/hooks/usePathLang'
import PricingTiersSection from '@/components/pricing/PricingTiersSection'
import PricingComparisonTable from '@/components/pricing/PricingComparisonTable'
import PricingCalculator from '@/components/pricing/PricingCalculator'
import AiShowcaseSections from './AiShowcaseSections'
import CinematicHomeBanner from './CinematicHomeBanner'

const LandingBelowFoldStatic = dynamic(() => import('./LandingBelowFoldStatic'), {
  ssr: true,
  loading: () => null,
})

const LandingFaqSection = dynamic(() => import('./LandingFaqSection'), {
  ssr: true,
  loading: () => null,
})

export type RestroHomeClientProps = {
  landingFaq?: { question?: string; answer?: string }[]
}

/** English at `/`, Arabic at `/ar` (no trailing slash normalized). */
function isHomePath(pathname: string, lp: string) {
  const p = pathname.replace(/\/+$/, '') || '/'
  if (!lp) return p === '/'
  return p === lp.replace(/\/+$/, '')
}

/** Marketing home — fully static (blog will come from Laravel Super Admin later). */
export default function RestroHomeClient({
  landingFaq: landingFaqProp = [],
}: RestroHomeClientProps) {
  const lang = usePathLang()
  const pathname = usePathname() || '/'
  const t = useTranslation(lang)
  const lp = langPrefix(lang)
  const isHomeLanding = isHomePath(pathname, lp)

  const [faqOpenIndex, setFaqOpenIndex] = useState<number | null>(null)

  const landingFaq = Array.isArray(landingFaqProp) ? landingFaqProp : []

  const faqItems =
    landingFaq.length > 0
      ? landingFaq.map((item) => ({ q: item.question ?? '', a: item.answer ?? '' }))
      : [
          { q: t('landing.faq1Q'), a: t('landing.faq1A') },
          { q: t('landing.faq2Q'), a: t('landing.faq2A') },
          { q: t('landing.faq3Q'), a: t('landing.faq3A') },
          { q: t('landing.faq4Q'), a: t('landing.faq4A') },
        ]

  return (
    <div className="main main--landing main--ar">
      <CinematicHomeBanner t={t} lp={lp} />

      <section className="ar-stats" aria-label={t('landing.arStatsAria')}>
        <div className="ar-container ar-stats-grid">
          <div>
            <span className="ar-stat-value">{t('landing.arStat1Value')}</span>
            <span className="ar-stat-label">{t('landing.arStat1Label')}</span>
          </div>
          <div>
            <span className="ar-stat-value">{t('landing.arStat2Value')}</span>
            <span className="ar-stat-label">{t('landing.arStat2Label')}</span>
          </div>
          <div>
            <span className="ar-stat-value">{t('landing.arStat3Value')}</span>
            <span className="ar-stat-label">{t('landing.arStat3Label')}</span>
          </div>
        </div>
      </section>

      <AiShowcaseSections />

      {isHomeLanding ? (
        <section className="ar-home-pricing" aria-labelledby="pricing-tiers-h2">
          <div className="ar-container">
            <PricingTiersSection
              title={t('landing.homePricingTitle')}
              sub={t('landing.homePricingSub')}
              lp={lp}
            />
            <PricingCalculator />
            <PricingComparisonTable />
          </div>
        </section>
      ) : null}

      <LandingBelowFoldStatic t={t} lp={lp} />

      {faqItems.length > 0 && (
        <LandingFaqSection
          t={t}
          faqItems={faqItems}
          faqOpenIndex={faqOpenIndex}
          setFaqOpenIndex={setFaqOpenIndex}
          lp={lp}
        />
      )}
    </div>
  )
}
